import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ShieldCheck, Award, Calendar, GraduationCap, ArrowRight } from "lucide-react";
import { API } from "../lib/api";

export default function VerifyCertificate() {
  const { prcId } = useParams();
  const [state, setState] = useState({ loading: true, valid: null, data: null });

  useEffect(() => {
    axios.get(`${API}/verify/${prcId}`)
      .then((r) => setState({ loading: false, valid: r.data.valid, data: r.data }))
      .catch(() => setState({ loading: false, valid: false, data: null }));
  }, [prcId]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-navy/60 text-sm">Verifying certificate…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="verify-page">
      {/* HEADER */}
      <header className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-7 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="verify-home-link">
            <div className="h-11 w-11 rounded-md bg-[#10B981]/20 flex items-center justify-center ring-1 ring-[#10B981]/40">
              <ShieldCheck className="h-5 w-5 text-[#10B981]" />
            </div>
            <div>
              <div className="eyebrow text-[#D4AF37]">Direct Hire Team</div>
              <h1 className="font-serif text-2xl mt-1">Certificate Verification</h1>
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        {state.valid ? (
          <div className="card-lux !p-10 md:!p-12 text-center" data-testid="verify-success">
            <div className="h-20 w-20 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto ring-2 ring-[#10B981]/40">
              <CheckCircle2 className="h-11 w-11 text-[#10B981]" />
            </div>
            <div className="eyebrow text-[#10B981] mt-7">Verified — Authentic</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy leading-tight">
              This is a genuine<br />Payroll Readiness Certificate.
            </h2>

            {/* Certificate facts */}
            <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-xl mx-auto text-left">
              <div className="rounded-md bg-[#F6FDF9] border border-[#10B981]/25 p-5" data-testid="verify-name">
                <div className="eyebrow text-[#10B981] flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5" /> Issued To
                </div>
                <div className="mt-2 font-serif text-xl text-navy">{state.data.name}</div>
              </div>
              <div className="rounded-md bg-[#FFFCF0] border border-[#D4AF37]/30 p-5" data-testid="verify-prc-id">
                <div className="eyebrow text-[#D4AF37] flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Verify ID
                </div>
                <div className="mt-2 font-mono text-lg text-navy font-bold tracking-wider">{state.data.prc_id}</div>
              </div>
              <div className="rounded-md bg-white border border-navy/10 p-5" data-testid="verify-date">
                <div className="eyebrow text-navy/55 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Issued On
                </div>
                <div className="mt-2 font-medium text-navy">{fmtDate(state.data.issued_at)}</div>
              </div>
              <div className="rounded-md bg-white border border-navy/10 p-5" data-testid="verify-track">
                <div className="eyebrow text-navy/55 flex items-center gap-1.5">
                  <GraduationCap className="h-3.5 w-3.5" /> Cohort Track
                </div>
                <div className="mt-2 font-medium text-navy">{state.data.track}</div>
              </div>
            </div>

            <p className="mt-10 text-sm text-navy/60 leading-relaxed max-w-lg mx-auto">
              The certificate holder has successfully completed Direct Hire Team's payroll readiness cohort program and is recognized as job-ready for entry-level payroll & office support roles.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/for-employers" className="btn-emerald" data-testid="verify-cta-employers">
                Hire from our talent pool <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/" className="btn-ghost" data-testid="verify-cta-home">Visit Direct Hire Team</Link>
            </div>
          </div>
        ) : (
          <div className="card-lux !p-10 text-center" data-testid="verify-failed">
            <div className="h-20 w-20 rounded-full bg-red-50 flex items-center justify-center mx-auto ring-2 ring-red-100">
              <XCircle className="h-11 w-11 text-red-600" />
            </div>
            <div className="eyebrow text-red-600 mt-7">Not Verified</div>
            <h2 className="mt-3 font-serif text-3xl text-navy leading-tight">
              We don't have a record of this certificate.
            </h2>
            <p className="mt-5 text-navy/65 leading-relaxed max-w-md mx-auto">
              The verification ID <span className="font-mono text-navy">{prcId}</span> does not match any active Payroll Readiness Certificate issued by Direct Hire Team.
            </p>
            <p className="mt-3 text-sm text-navy/55 leading-relaxed max-w-md mx-auto">
              If you believe this is an error, please contact <a href="mailto:christopher@directhireteam.com" className="text-[#10B981] hover:underline">christopher@directhireteam.com</a>.
            </p>
            <Link to="/" className="btn-emerald mt-8 inline-flex" data-testid="verify-back-home">
              Back to homepage <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        <p className="text-center text-xs text-navy/40 mt-10">
          Verifications are public and tamper-proof. Each PRC is uniquely issued and digitally signed.
        </p>
      </main>
    </div>
  );
}
